// Featured cases are played through the real pro-gen game screen. The backend
// mints their session id in its own namespace (`featured:{slug}`, see
// `backend/featured_cases/compat.py:session_id_for`) so a single prefix check
// is enough to route every game-screen call at the URL layer instead of
// branching inside components.
//
// This module holds the primitive only: `gameAPI.js` owns the URL switch and
// `gameStore.js` exposes the selector, and both import from here so the prefix
// is written down once.
export const FEATURED_SESSION_PREFIX = 'featured:';

/**
 * Whether an id merely carries the namespace prefix, slug or not. Only the
 * malformed-id guard in `gameAPI.js` needs this: a bare `featured:` is not a
 * usable featured id, but it must not be silently treated as pro-gen either.
 */
export const hasFeaturedSessionPrefix = (sessionId) => (
  typeof sessionId === 'string' && sessionId.startsWith(FEATURED_SESSION_PREFIX)
);

/**
 * The featured slug behind a session id, or `null` for a pro-gen (UUID) id or
 * a malformed `featured:` with nothing after the prefix.
 */
export const featuredSlug = (sessionId) => (
  hasFeaturedSessionPrefix(sessionId)
    ? sessionId.slice(FEATURED_SESSION_PREFIX.length) || null
    : null
);

/**
 * A usable featured session id — prefix AND slug. Defined in terms of
 * `featuredSlug` so the two can never disagree: anything this calls featured
 * is guaranteed to produce a compat URL.
 */
export const isFeaturedSessionId = (sessionId) => featuredSlug(sessionId) !== null;

export const featuredSessionId = (slug) => {
  if (!slug) {
    throw new Error('featuredSessionId requires a slug');
  }
  return `${FEATURED_SESSION_PREFIX}${slug}`;
};
